import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiResponse } from '../types/api';

export interface WeatherHistoryRecord {
  timestamp: string;
  temperature: number;
  relative_humidity: number;
  wind_speed: number;
  wind_direction: number;
  fire_danger_rating: number;
  drought_factor?: number;
}

interface WeatherHistoryListResponse {
  data: WeatherHistoryRecord[];
  count: number;
}

export interface WeatherStatistics {
  temperature: {
    mean: number;
    min: number;
    max: number;
    percentile_95: number;
    percentile_99: number;
  };
  relative_humidity: {
    mean: number;
    min: number;
    max: number;
  };
  wind_speed: {
    mean: number;
    max: number;
    dominant_direction: string;
  };
  fire_danger_rating: {
    mean: number;
    max: number;
    percentile_95: number;
    percentile_99: number;
  };
  drought_factor: {
    mean: number;
    max: number;
    min: number;
  };
  /** Keetch-Byram Drought Index (mm) on the last day the project has one; null until built. */
  kbdi?: {
    latest: number | null;
    date: string | null;
  } | null;
}

export interface MonthlyStatistics {
  month: number;
  avg_temperature: number;
  avg_daily_max_temperature: number;
  avg_humidity: number;
  avg_wind_speed: number;
  dominant_wind_direction: string;
  avg_drought_factor: number | null;
  avg_fire_danger: number;
  fire_danger_min?: number;
  fire_danger_q1?: number;
  fire_danger_median?: number;
  fire_danger_q3?: number;
  fire_danger_max?: number;
}

export interface WindDirectionFrequency {
  direction: string;
  frequency: number;
  avg_speed: number;
}

export const useWeatherHistory = (projectId: string, startTime?: string, endTime?: string) => {
  const params = new URLSearchParams({ project_id: projectId });
  if (startTime) params.append('start_time', startTime);
  if (endTime) params.append('end_time', endTime);
  
  const { data } = useQuery({
    queryKey: ['weatherHistory', projectId, startTime, endTime],
    queryFn: () => api.request<ApiResponse<WeatherHistoryListResponse | WeatherHistoryRecord[]>>(`/api/weather/history?${params.toString()}`),
    enabled: !!projectId,
  });

  const payload = data?.data as unknown;
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object' && Array.isArray((payload as WeatherHistoryListResponse).data)) {
    return (payload as WeatherHistoryListResponse).data;
  }

  return [];
};

export const useWeatherStatistics = (projectId: string, startTime?: string, endTime?: string) => {
  const params = new URLSearchParams({ project_id: projectId });
  if (startTime) params.append('start_time', startTime);
  if (endTime) params.append('end_time', endTime);
  
  return useQuery({
    queryKey: ['weatherStatistics', projectId, startTime, endTime],
    queryFn: async () => {
      const response = await api.request<ApiResponse<WeatherStatistics>>(`/api/weather/statistics?${params.toString()}`);
      return response.data;
    },
    enabled: !!projectId,
  });
};

export const useMonthlyWeatherStatistics = (projectId: string) => {
  const query = useQuery({
    queryKey: ['monthlyWeatherStatistics', projectId],
    queryFn: () => api.request<ApiResponse<MonthlyStatistics[]>>(`/api/weather/statistics/monthly?project_id=${projectId}`),
    enabled: !!projectId,
  });
  
  return {
    data: query.data?.data || [],
    isLoading: query.isLoading,
  };
};

export const useWindDirectionFrequency = (projectId: string, month?: number | null) => {
  const params = new URLSearchParams({ project_id: projectId });
  if (month) params.append('month', month.toString());
  
  const query = useQuery({
    queryKey: ['windDirectionFrequency', projectId, month],
    queryFn: () => api.request<ApiResponse<WindDirectionFrequency[]>>(`/api/weather/statistics/wind-direction?${params.toString()}`),
    enabled: !!projectId,
  });
  
  return {
    data: query.data?.data || [],
    isLoading: query.isLoading,
  };
};

export const getExtremeWeatherDays = async (projectId: string, limit: number = 5) => {
  const response = await api.request<ApiResponse<any[]>>(
    `/api/weather/history/extreme?project_id=${projectId}&limit=${limit}`
  );
  return response.data || [];
};

export interface DailyFDIRecord {
  date: string;
  fdi: number;
}

export const useDailyFDI = (projectId: string) => {
  return useQuery({
    queryKey: ['dailyFDI', projectId],
    queryFn: async () => {
      const response = await api.request<ApiResponse<DailyFDIRecord[]>>(
        `/api/weather/history/daily-fdi?project_id=${projectId}`
      );
      return response?.data ?? [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useWeatherHistoryDateRange = (projectId: string) => {
  return useQuery({
    queryKey: ['weatherHistoryDateRange', projectId],
    queryFn: async () => {
      const response = await api.request<ApiResponse<{ min_date: string | null; max_date: string | null }>>(
        `/api/weather/history/date-range?project_id=${projectId}`
      );
      const data = response.data;
      
      if (!data || !data.min_date || !data.max_date) {
        return { minDate: null, maxDate: null };
      }
      
      return {
        minDate: new Date(data.min_date),
        maxDate: new Date(data.max_date),
      };
    },
    enabled: !!projectId,
  });
};
